import { createInstance } from "i18next";
import { initReactI18next } from "react-i18next";
import { LOCALES, LOCALE_CODES, isLocaleCode, type LocaleCode } from "./locales";
import catalogUrls from "virtual:qbx-catalog-urls";
import en from "./locales/en";

type Catalog = { [N in keyof typeof en]: Record<keyof typeof en[N], string> };
const record = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object" && !Array.isArray(value);
const parameters = (value: string) => [...value.matchAll(/{{\s*(\w+)\s*}}/g)].map(match => match[1]).sort().join("|");

/** A registry-authored filesystem token is literal text, never translator markup. */
export function invalidCatalogMarkup(namespace: string, key: string, text: string): boolean {
  if (namespace === "applicationMetadata" && key === "apps.nzbget.features.per-user-binary") {
    const token = "/opt/<user>/NZBGet";
    if (text.split(token).length !== 2) return true;
    text = text.replace(token, "");
  }
  if (namespace === "apiMetadata") {
    const tokens: Record<string, string> = {
      "api.catalog.admin-delete-protection-recipe.description": "?app=<short-name>",
      "api.catalog.admin-delete-protection-deletes.description": "?limit=<n>",
      "api.catalog.admin-security-fail2ban-app-jails.description": "qbx-<app>",
    };
    if (Object.prototype.hasOwnProperty.call(tokens, key)) {
      const token = tokens[key]!;
      if (text.split(token).length !== 2) return true;
      text = text.replace(token, "");
    }
  }
  return /<\/?[A-Za-z][^>]*>/.test(text);
}

/** Reject HTML error pages, partial/corrupt catalogs and unexpected structured data. */
export function validateCatalog(value: unknown): Catalog {
  if (!record(value) || Object.keys(value).length !== Object.keys(en).length) throw new Error("Invalid catalog");
  for (const [namespace, source] of Object.entries(en)) {
    const translated = value[namespace];
    if (!record(translated) || Object.keys(translated).length !== Object.keys(source).length) throw new Error("Invalid namespace");
    for (const [key, original] of Object.entries(source)) {
      const message = translated[key];
      if (!Object.prototype.hasOwnProperty.call(translated, key) || typeof message !== "string" || (!message.trim() && !(namespace === "applicationMetadata" && key === "apps.rclone.installConfig.token.label" && original === "" && message === ""))
        || (namespace === "applicationMetadata" && key === "apps.rclone.installConfig.token.label" && message !== "")
        || invalidCatalogMarkup(namespace, key, message) || parameters(message) !== parameters(original)) throw new Error("Invalid message");
    }
  }
  return value as Catalog;
}

export function createDashboardEngine() {
  const i18n = createInstance();
  const cache = new Map<LocaleCode, Catalog>();
  void i18n.use(initReactI18next).init({
    lng: "en", fallbackLng: "en", supportedLngs: [...LOCALE_CODES], load: "currentOnly",
    ns: Object.keys(en), defaultNS: "language", resources: { en }, initAsync: false,
    interpolation: { escapeValue: false }, saveMissing: false,
    react: { useSuspense: false, transSupportBasicHtmlNodes: false },
  });
  return {
    i18n,
    load: async (locale: LocaleCode) => {
      if (!isLocaleCode(locale) || (locale !== "en" && !Object.prototype.hasOwnProperty.call(catalogUrls, locale))) throw new Error("Unsupported locale");
      let resources: Catalog | undefined = locale === "en" ? en : cache.get(locale);
      if (!resources) {
        const url = new URL(catalogUrls[locale]!, location.href);
        if (url.origin !== location.origin || !["http:", "https:"].includes(url.protocol)) throw new Error("Invalid catalog origin");
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15_000);
        try {
          // Each failed attempt remains retryable: only validated data is cached,
          // and fetch does not use the browser's permanent module failure cache.
          const response = await fetch(url.href, { cache: "no-store", credentials: "omit", redirect: "error", signal: controller.signal });
          if (!response.ok || !/^application\/json(?:\s*;|$)/i.test(response.headers.get("content-type") ?? "")) throw new Error("Catalog unavailable");
          resources = validateCatalog(await response.json());
          cache.set(locale, resources);
        } finally { clearTimeout(timeout); }
      }
      const ready = resources;
      return () => {
        for (const [namespace, catalog] of Object.entries(ready)) i18n.addResourceBundle(locale, namespace, catalog, true, true);
        void i18n.changeLanguage(locale);
        if (typeof document !== "undefined") {
          document.documentElement.lang = locale;
          document.documentElement.dir = LOCALES.find(entry => entry.code === locale)!.direction;
        }
      };
    },
  };
}
